// API tokens page — developers / CI lanes mint tokens here and use
// them as the password in `docker login`, `npm publish --auth`,
// `helm registry login`, etc. Plaintext is shown ONCE at creation;
// only the hash is stored.
//
// Item 60 redesign (2026-05-04): the create form is bundle-driven
// (Pull / Publish / Admin) instead of raw `docker:pull` / `read` /
// `write` chips. An optional Advanced section restricts to specific
// package formats; the server expands the (bundle, formats) pair into
// the granular scope strings it persists.
//
// After creation the success card includes a "Ready-to-use" snippet
// block — a copy-pasteable npm / Docker / Maven / Helm snippet keyed
// to the formats the operator selected — so a CI engineer never has
// to chase docs to wire the token into their pipeline.

import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Plus, Trash2, Copy, Key, AlertTriangle, Check,
  ChevronDown, ChevronRight, Download, Upload, Settings as Cog,
} from 'lucide-react';
import { Card, CardBody, CardFooter, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import { FORMATS } from '../lib/formats';
import { Spinner } from '../components/ui/Spinner';
import {
  useTokensPaginated, createToken, revokeToken,
  type CreatedToken, type Token, type TokenBundle,
} from '../api/tokens';
import { useWhoami } from '../api/client';

type BundleDef = {
  id: TokenBundle;
  label: string;
  description: string;
  recommended?: boolean;
  icon: typeof Download;
  scopeChips: string[];
};

function bundleDefs(t: ReturnType<typeof useTranslation>['t']): BundleDef[] {
  return [
    {
      id: 'pull',
      label: t('tokens.bundles.pull.label'),
      description: t('tokens.bundles.pull.description'),
      icon: Download,
      scopeChips: ['read'],
    },
    {
      id: 'publish',
      label: t('tokens.bundles.publish.label'),
      description: t('tokens.bundles.publish.description'),
      recommended: true,
      icon: Upload,
      scopeChips: ['read', 'write'],
    },
    {
      id: 'admin',
      label: t('tokens.bundles.admin.label'),
      description: t('tokens.bundles.admin.description'),
      icon: Cog,
      scopeChips: ['read', 'write', 'admin'],
    },
  ];
}

// Snippets are templated against the host the page is served from so
// a copy from staging.orbitalreg.io produces a snippet that points at
// staging — no manual host edit needed.
function snippetForFormat(format: string, host: string, token: string): string | null {
  switch (format) {
    case 'npm':
      return `# ~/.npmrc — npm / pnpm / yarn
//${host}/npm/:_authToken=${token}
@your-scope:registry=https://${host}/npm/<project>/<repo>/`;
    case 'docker':
    case 'ollama':
    case 'kustomize':
      return `# Docker / OCI login
echo "${token}" | docker login ${host} -u ci --password-stdin`;
    case 'maven':
      return `<!-- ~/.m2/settings.xml -->
<server>
  <id>orbitalreg</id>
  <username>ci</username>
  <password>${token}</password>
</server>`;
    case 'pypi':
      return `# ~/.pypirc
[orbitalreg]
repository = https://${host}/pypi/<project>/<repo>/legacy/
username = __token__
password = ${token}`;
    case 'helm':
      return `helm registry login ${host} --username ci --password '${token}'`;
    case 'nuget':
      return String.raw`dotnet nuget add source "https://${host}/nuget/<project>/<repo>/" \
  --name orbitalreg --username ci --password '${token}' --store-password-in-clear-text`;
    case 'cargo':
      return `# ~/.cargo/credentials.toml
[registries.orbitalreg]
token = "Bearer ${token}"`;
    case 'rubygems':
      return `# ~/.gem/credentials (chmod 0600)
:orbitalreg: Bearer ${token}`;
    case 'go':
      return `# go env
GOPROXY=https://ci:${token}@${host}/go/<project>/<repo>/,direct`;
    case 'terraform':
      return `# ~/.terraformrc
credentials "${host}" {
  token = "${token}"
}`;
    case 'composer':
      return `# auth.json (project root)
{ "http-basic": { "${host}": { "username": "ci", "password": "${token}" } } }`;
    case 'rpm':
    case 'debian':
    case 'alpine':
      return `# Generic HTTP basic — use your distro's repo config
curl -u "ci:${token}" https://${host}/${format}/<project>/<repo>/...`;
    default:
      return null;
  }
}

// Universal "any format" snippet: a Bearer header curl example. Always
// shown at the bottom of the snippet block as a fallback for formats
// without a templated snippet.
function snippetForGeneric(host: string, token: string): string {
  return String.raw`# Any format — Bearer token
curl -H "Authorization: Bearer ${token}" \
  https://${host}/api/v1/artifacts/<repo_id>/<path>`;
}

export function Tokens() {
  const { t } = useTranslation();
  // Item 97 Phase C: cursor-paginated list. `tokens` accumulates pages
  // as the user clicks "Show more"; `hasMore` flips false on the last
  // page so the button hides itself.
  const { items: tokens, mutate, isLoading, isValidating, hasMore, loadMore } =
    useTokensPaginated();
  // The admin bundle needs org_admin server-side (audit 2026-10-15
  // F-01); only offer it to callers who can actually mint it.
  const { user } = useWhoami();
  const isOrgAdmin = !!(user?.roles?.includes('org_admin') || user?.roles?.includes('admin'));
  const bundleDefinitions = useMemo(
    () => bundleDefs(t).filter((b) => b.id !== 'admin' || isOrgAdmin),
    [t, isOrgAdmin],
  );
  const [name, setName] = useState('');
  const [bundle, setBundle] = useState<TokenBundle>('publish');
  const [formats, setFormats] = useState<string[]>([]);
  const [showFormats, setShowFormats] = useState(false);
  const [expiresIn, setExpiresIn] = useState('');
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<CreatedToken | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const host = typeof globalThis === 'undefined' ? 'orbitalreg.example.com' : globalThis.location.host;

  const onCreate = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const minted = await createToken({
        name: name.trim(),
        bundle,
        formats: formats.length > 0 ? formats : undefined,
        expiresIn: expiresIn || undefined,
      });
      setCreated(minted);
      setName('');
      setExpiresIn('');
      setFormats([]);
      setShowFormats(false);
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const onRevoke = async (id: string) => {
    if (!confirm(t('tokens.confirmRevoke'))) return;
    await revokeToken(id);
    await mutate();
  };

  const onCopy = async (key: string, value: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied((c) => (c === key ? null : c)), 2000);
  };

  const toggleFormat = (id: string) => {
    setFormats((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  // Snippet selection for the success card. If the operator restricted
  // to specific formats we show a snippet per chosen format; otherwise
  // we show the popular handful (npm / docker / maven / pypi / helm)
  // plus the generic Bearer fallback.
  const snippetFormats = useMemo(() => {
    if (formats.length > 0) return formats;
    return ['npm', 'docker', 'maven', 'pypi', 'helm'];
  }, [formats]);

  return (
    <div className="space-y-6">
      <div>
        <p className="orb-eyebrow text-accent/80 mb-2">{t('tokens.eyebrow')}</p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight">{t('tokens.title')}</h1>
        <p className="text-fg-muted text-sm mt-2 max-w-2xl">
          <Trans i18nKey="tokens.intro" components={[<code key={0} className="orb-code" />]} />
        </p>
      </div>

      {created && (
        <Card className="border-success/40 bg-success/5">
          <CardHeader
            title={t('tokens.created.title')}
            description={t('tokens.created.description')}
          />
          <CardBody className="space-y-4">
            <div className="flex items-center gap-2 p-3 rounded-md bg-bg-inset border border-border font-mono text-sm break-all">
              <Key className="h-4 w-4 text-success shrink-0" />
              <span className="flex-1">{created.token}</span>
              <button
                type="button"
                onClick={() => onCopy('plaintext', created.token)}
                className="inline-flex items-center gap-1 px-3 h-8 rounded-md border border-border hover:bg-bg-hover text-fg-muted hover:text-fg text-sm shrink-0"
              >
                {copied === 'plaintext' ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
                {copied === 'plaintext' ? t('tokens.created.copied') : t('tokens.created.copy')}
              </button>
            </div>
            <p className="text-xs text-fg-muted">
              {t('tokens.meta.name')} <span className="text-fg">{created.name}</span>
              {created.bundle ? (
                <>
                  {' '}· {t('tokens.meta.bundle')} <span className="text-fg">{labelForBundle(created.bundle, t)}</span>
                </>
              ) : null}
              {created.formats && created.formats.length > 0 ? (
                <> · {t('tokens.meta.formats')} {created.formats.map((f) => labelForFormat(f)).join(', ')}</>
              ) : null}
            </p>
            <div className="space-y-3">
              <p className="text-xs font-medium uppercase tracking-wide text-fg-muted">{t('tokens.created.snippetsTitle')}</p>
              {snippetFormats.map((f) => {
                const snip = snippetForFormat(f, host, created.token);
                if (!snip) return null;
                const key = `snip-${f}`;
                return (
                  <SnippetBlock
                    key={key}
                    label={labelForFormat(f)}
                    body={snip}
                    copied={copied === key}
                    onCopy={() => onCopy(key, snip)}
                  />
                );
              })}
              <SnippetBlock
                label={t('tokens.created.genericSnippetLabel')}
                body={snippetForGeneric(host, created.token)}
                copied={copied === 'snip-generic'}
                onCopy={() => onCopy('snip-generic', snippetForGeneric(host, created.token))}
              />
            </div>
          </CardBody>
          <CardFooter className="flex justify-end">
            <Button variant="secondary" onClick={() => setCreated(null)}>
              {t('tokens.created.dismiss')}
            </Button>
          </CardFooter>
        </Card>
      )}

      <Card>
        <CardHeader title={t('tokens.create.title')} description={t('tokens.create.description')} />
        <CardBody className="space-y-5">
          <div>
            <label htmlFor="token-name" className="block text-sm font-medium text-fg mb-1.5">{t('tokens.create.nameLabel')}</label>
            <Input
              id="token-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('tokens.create.namePlaceholder')}
            />
            <p className="text-xs text-fg-subtle mt-1">{t('tokens.create.nameHint')}</p>
          </div>

          <fieldset className="border-0 p-0 m-0 min-w-0">
            <legend className="block text-sm font-medium text-fg mb-2">{t('tokens.create.bundleLabel')}</legend>
            <div className="grid grid-cols-1 gap-2">
              {bundleDefinitions.map((b) => {
                const Icon = b.icon;
                const active = bundle === b.id;
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setBundle(b.id)}
                    className={
                      'text-left rounded-lg border p-4 transition-colors flex items-start gap-3 ' +
                      (active
                        ? 'border-accent bg-accent/5 ring-1 ring-accent/40'
                        : 'border-border hover:border-border-strong hover:bg-bg-hover/60')
                    }
                  >
                    <Icon className={'h-5 w-5 mt-0.5 shrink-0 ' + (active ? 'text-accent' : 'text-fg-muted')} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-fg">{b.label}</span>
                        {b.recommended && <Badge tone="success">{t('tokens.bundles.publish.recommended')}</Badge>}
                      </div>
                      <p className="text-xs text-fg-muted mt-1 leading-relaxed">{b.description}</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {b.scopeChips.map((s) => (
                          <span key={s} className="orb-code text-[10px]">{s}</span>
                        ))}
                      </div>
                    </div>
                    <div
                      className={
                        'h-4 w-4 rounded-full border-2 mt-1 shrink-0 ' +
                        (active ? 'border-accent bg-accent' : 'border-border-strong')
                      }
                      aria-hidden
                    />
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div className="rounded-md border border-border">
            <button
              type="button"
              onClick={() => setShowFormats((s) => !s)}
              className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-fg hover:bg-bg-hover/60"
            >
              <span className="flex items-center gap-2">
                {showFormats ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                {t('tokens.create.restrictFormats')}
              </span>
              <span className="text-xs text-fg-muted font-normal">
                {formats.length === 0
                  ? t('tokens.create.allFormats')
                  : t('tokens.create.selectedCount', { count: formats.length })}
              </span>
            </button>
            {showFormats && (
              <div className="px-4 pb-4 pt-1">
                <p className="text-xs text-fg-muted mb-2">
                  {t('tokens.create.restrictHint')}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {FORMATS.map((f) => {
                    const active = formats.includes(f.id);
                    return (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => toggleFormat(f.id)}
                        className={
                          'px-2.5 h-7 rounded-md text-xs border transition-colors ' +
                          (active
                            ? 'border-accent text-accent bg-accent/10'
                            : 'border-border text-fg-muted hover:text-fg hover:border-border-strong')
                        }
                      >
                        {f.label}
                      </button>
                    );
                  })}
                </div>
                {formats.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setFormats([])}
                    className="mt-2 text-xs text-fg-muted hover:text-fg underline"
                  >
                    {t('tokens.create.clearSelection')}
                  </button>
                )}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="token-expires-in" className="block text-sm font-medium text-fg mb-1.5">
              {t('tokens.create.expiresLabel')} <span className="text-fg-subtle font-normal">{t('tokens.create.expiresOptional')}</span>
            </label>
            <Input id="token-expires-in" value={expiresIn} onChange={(e) => setExpiresIn(e.target.value)} placeholder={t('tokens.create.expiresPlaceholder')} />
          </div>

          <div className="rounded-md bg-warning/5 border border-warning/30 p-3 flex items-start gap-2 text-sm">
            <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
            <p className="text-fg-muted">
              <Trans i18nKey="tokens.create.warning" components={[<span key={0} className="text-fg font-medium" />]} />
            </p>
          </div>
        </CardBody>
        <CardFooter className="flex justify-end">
          <Button variant="primary" disabled={busy || !name.trim()} onClick={onCreate}>
            <Plus className="h-4 w-4" />
            {t('tokens.create.submit')}
          </Button>
        </CardFooter>
      </Card>

      {err && (
        <Card className="border-danger/30 bg-danger/5">
          <CardBody>
            <p className="text-danger font-mono text-sm">{err}</p>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader
          title={`${t('tokens.table.title', { count: tokens.length })}${hasMore ? '+' : ''}`}
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">{t('tokens.table.name')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('tokens.table.scope')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('tokens.table.created')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('tokens.table.lastUsed')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('tokens.table.expires')}</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              <TokenTableRows
                isLoading={isLoading}
                tokens={tokens}
                onRevoke={onRevoke}
              />
            </tbody>
          </table>
        </div>
        {hasMore && (
          <CardBody>
            <div className="flex justify-center">
              <Button
                variant="ghost"
                onClick={loadMore}
                disabled={isValidating}
              >
                {isValidating ? <Spinner size="xs" /> : t('tokens.showMore')}
              </Button>
            </div>
          </CardBody>
        )}
      </Card>
    </div>
  );
}

interface TokenTableRowsProps {
  readonly isLoading: boolean;
  readonly tokens: Token[];
  readonly onRevoke: (id: string) => void;
}

const TOKEN_SKELETON_KEYS = Array.from({ length: 4 }, () => crypto.randomUUID());

function TokenTableRows({ isLoading, tokens, onRevoke }: TokenTableRowsProps) {
  const { t } = useTranslation();
  if (isLoading && tokens.length === 0) {
    return (
      <>
        {TOKEN_SKELETON_KEYS.map((key) => (
          <tr key={key} className="border-b border-border/60 last:border-0 align-top">
            <td className="px-5 py-3"><Skeleton width="10rem" /></td>
            <td className="px-5 py-3"><Skeleton width="7rem" height="1.25rem" rounded="9999px" /></td>
            <td className="px-5 py-3"><Skeleton width="5rem" /></td>
            <td className="px-5 py-3"><Skeleton width="5rem" /></td>
            <td className="px-5 py-3"><Skeleton width="5rem" /></td>
            <td className="px-5 py-3"><Skeleton width="2rem" height="2rem" rounded="0.375rem" /></td>
          </tr>
        ))}
      </>
    );
  }

  if (tokens.length === 0) {
    return (
      <tr>
        <td colSpan={6} className="px-5 py-12 text-center text-fg-muted text-sm">
          {t('tokens.table.empty')}
        </td>
      </tr>
    );
  }

  return (
    <>
      {tokens.map((tok) => (
        <tr key={tok.id} className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40 align-top">
          <td className="px-5 py-3 text-fg">{tok.name}</td>
          <td className="px-5 py-3"><ScopeCell token={tok} /></td>
          <td className="px-5 py-3 text-fg-muted text-xs">
            {new Date(tok.createdAt).toLocaleDateString()}
          </td>
          <td className="px-5 py-3 text-fg-muted text-xs">
            {tok.lastUsedAt ? new Date(tok.lastUsedAt).toLocaleDateString() : '—'}
          </td>
          <td className="px-5 py-3 text-fg-muted text-xs">
            {tok.expiresAt ? new Date(tok.expiresAt).toLocaleDateString() : t('tokens.table.never')}
          </td>
          <td className="px-5 py-3">
            <button
              type="button"
              onClick={() => onRevoke(tok.id)}
              className="h-8 w-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:bg-danger/10 hover:text-danger"
              title={t('tokens.table.revoke')}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </td>
        </tr>
      ))}
    </>
  );
}

function labelForBundle(b: TokenBundle, t: ReturnType<typeof useTranslation>['t']): string {
  switch (b) {
    case 'pull': return t('tokens.bundles.pull.label');
    case 'publish': return t('tokens.bundles.publish.label');
    case 'admin': return t('tokens.bundles.admin.label');
  }
}

function labelForFormat(id: string): string {
  const f = FORMATS.find((x) => x.id === id);
  return f ? f.label : id;
}

function bundleBadgeTone(bundle: TokenBundle): 'warning' | 'success' | 'neutral' {
  if (bundle === 'admin') return 'warning';
  if (bundle === 'publish') return 'success';
  return 'neutral';
}

function ScopeCell({ token }: Readonly<{ token: Token }>) {
  const { t } = useTranslation();
  if (token.bundle) {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Badge tone={bundleBadgeTone(token.bundle)}>
            {labelForBundle(token.bundle, t)}
          </Badge>
        </div>
        {token.formats && token.formats.length > 0 && (
          <p className="text-[11px] text-fg-muted">
            {t('tokens.meta.formats')} {token.formats.map((f) => labelForFormat(f)).join(', ')}
          </p>
        )}
      </div>
    );
  }
  // Advanced / unknown bundle: fall back to raw scope chips like the
  // legacy UI did, so a token minted via the CLI / API with custom
  // scopes still surfaces something useful.
  return (
    <div className="flex flex-wrap gap-1">
      {token.scopes.map((s) => (
        <Badge key={s} tone="neutral">{s}</Badge>
      ))}
    </div>
  );
}

function SnippetBlock({
  label, body, copied, onCopy,
}: Readonly<{
  label: string;
  body: string;
  copied: boolean;
  onCopy: () => void;
}>) {
  const { t } = useTranslation();
  return (
    <div className="rounded-md border border-border bg-bg-inset overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 bg-bg-hover/40 border-b border-border">
        <span className="text-xs font-medium text-fg">{label}</span>
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex items-center gap-1 px-2 h-6 rounded text-xs text-fg-muted hover:text-fg hover:bg-bg-hover"
        >
          {copied ? <Check className="h-3 w-3 text-success" /> : <Copy className="h-3 w-3" />}
          {copied ? t('tokens.created.copied') : t('tokens.created.copy')}
        </button>
      </div>
      <pre className="px-3 py-2 text-[11px] font-mono text-fg-muted whitespace-pre-wrap break-all leading-relaxed">{body}</pre>
    </div>
  );
}
